import {
  Loader2,
  Mic,
  Plus,
  RefreshCw,
  Save,
  Scan,
  Trash2,
  Type,
  Volume2,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import { API_BASE_URL } from "../../shared/constants";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Modal from "../ui/Modal";
import Select from "../ui/Select";
import Textarea from "../ui/Textarea";

interface VideoText {
  id: string;
  text: string;
  startTime: number;
  endTime: number;
  position: string;
  fontSize?: number;
  color?: string;
}

interface VideoEditorModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    texts: VideoText[];
    script: string;
    pronunciationGuide?: Record<string, string>;
    editedTranscription?: string;
  }) => Promise<void>;
  postId: number;
  videoDuration: number;
  initialTexts?: VideoText[];
  initialScript?: string;
  loading?: boolean;
  videoUrl?: string;
  videoFilename?: string;
}

const VideoEditorModal: React.FC<VideoEditorModalProps> = ({
  open,
  onClose,
  onSave,
  postId,
  videoDuration,
  initialTexts = [],
  initialScript = "",
  loading = false,
  videoUrl,
}) => {
  const { t } = useTranslation();
  const [videoTexts, setVideoTexts] = useState<VideoText[]>(initialTexts);
  const [videoScript, setVideoScript] = useState(initialScript);
  const [pronunciationGuide, setPronunciationGuide] = useState<
    Record<string, string>
  >({});
  const [saving, setSaving] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  // Estados para extração de textos e transcrição
  const [detectedTexts, setDetectedTexts] = useState<any[]>([]);
  const [transcription, setTranscription] = useState<string>("");
  const [editedTranscription, setEditedTranscription] = useState<string>("");
  const [extractingTexts, setExtractingTexts] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [regeneratingAudio, setRegeneratingAudio] = useState(false);

  useEffect(() => {
    if (open) {
      setVideoTexts(initialTexts);
      setVideoScript(initialScript);
      setPronunciationGuide({});
      setDetectedTexts([]);
      setTranscription("");
      setEditedTranscription("");
    }
  }, [open, initialTexts, initialScript]);

  const handleExtractTexts = async () => {
    setExtractingTexts(true);
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("access_token");
      const endpoint = `/posts/${postId}/extract-video-texts`;
      const fullUrl = endpoint.startsWith("http")
        ? endpoint
        : `${API_BASE_URL}${endpoint}`;

      const response = await fetch(fullUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: t("Erro desconhecido") }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setDetectedTexts(result.texts || []);
        // Adicionar textos detectados à lista de textos editáveis
        const newTexts = (result.texts || []).map(
          (detected: any, index: number) => ({
            id: `detected_${Date.now()}_${index}`,
            text: detected.text,
            startTime: detected.start_time || 0,
            endTime: detected.end_time || videoDuration,
            position: "bottom-center",
            fontSize: 24,
            color: "#FFFFFF",
          }),
        );
        setVideoTexts([...videoTexts, ...newTexts]);
      }
    } catch (error: any) {
      console.error("Erro ao extrair textos:", error);
      alert(
        t("Erro ao extrair textos: {message}", {
          message: error.message || t("Erro desconhecido"),
        }),
      );
    } finally {
      setExtractingTexts(false);
    }
  };

  const handleTranscribeAudio = async () => {
    setTranscribing(true);
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("access_token");
      const endpoint = `/posts/${postId}/transcribe-video-audio`;
      const fullUrl = endpoint.startsWith("http")
        ? endpoint
        : `${API_BASE_URL}${endpoint}`;

      const response = await fetch(fullUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          use_whisper: false,
          language_code: "pt-BR",
        }),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: t("Erro desconhecido") }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setTranscription(result.text || "");
        setEditedTranscription(result.text || "");
        setVideoScript(result.text || "");
      }
    } catch (error: any) {
      console.error("Erro ao transcrever áudio:", error);
      alert(
        t("Erro ao transcrever áudio: {message}", {
          message: error.message || t("Erro desconhecido"),
        }),
      );
    } finally {
      setTranscribing(false);
    }
  };

  const handleRegenerateAudio = async () => {
    if (!editedTranscription.trim()) {
      alert(
        t("Por favor, edite o texto transcrito antes de regenerar o áudio."),
      );
      return;
    }

    setRegeneratingAudio(true);
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("access_token");
      const endpoint = `/posts/${postId}/regenerate-video-audio`;
      const fullUrl = endpoint.startsWith("http")
        ? endpoint
        : `${API_BASE_URL}${endpoint}`;

      const response = await fetch(fullUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          text: editedTranscription,
          language_code: "pt-BR",
        }),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: t("Erro desconhecido") }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        alert(t("Áudio regenerado com sucesso! O vídeo será atualizado."));
        // Recarregar a página ou atualizar o vídeo
        window.location.reload();
      }
    } catch (error: any) {
      console.error("Erro ao regenerar áudio:", error);
      alert(
        t("Erro ao regenerar áudio: {message}", {
          message: error.message || t("Erro desconhecido"),
        }),
      );
    } finally {
      setRegeneratingAudio(false);
    }
  };

  const handleAddText = () => {
    const newText: VideoText = {
      id: Date.now().toString(),
      text: "",
      startTime: 0,
      endTime: videoDuration,
      position: "bottom-center",
      fontSize: 24,
      color: "#FFFFFF",
    };
    setVideoTexts([...videoTexts, newText]);
  };

  const handleRemoveText = (id: string) => {
    setVideoTexts(videoTexts.filter((t) => t.id !== id));
  };

  const handleUpdateText = (id: string, field: keyof VideoText, value: any) => {
    setVideoTexts(
      videoTexts.map((t) => (t.id === id ? { ...t, [field]: value } : t)),
    );
  };

  const handleUpdatePronunciation = (word: string, pronunciation: string) => {
    setPronunciationGuide((prev) => ({
      ...prev,
      [word]: pronunciation,
    }));
  };

  const extractWordsFromScript = (script: string): string[] => {
    // Extrair palavras que podem ter problemas de pronúncia
    const words = script.split(/\s+/).filter((w) => w.length > 3);
    return Array.from(new Set(words));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        texts: videoTexts,
        script: videoScript,
        pronunciationGuide:
          Object.keys(pronunciationGuide).length > 0
            ? pronunciationGuide
            : undefined,
        editedTranscription: editedTranscription || undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  const scriptWords = videoScript ? extractWordsFromScript(videoScript) : [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("Editor de Vídeo - Post #{postId}", { postId })}
      maxWidth="max-w-4xl"
      footer={
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={saving || loading}
          >
            {t("Cancelar")}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            icon={
              saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )
            }
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? t("Salvando...") : t("Salvar Alterações")}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Preview do Vídeo com Textos */}
        {videoUrl && (
          <div className="border-b pb-4">
            <h3 className="text-lg font-semibold mb-3">
              {t("Preview do Vídeo")}
            </h3>
            <div
              className="relative bg-black rounded-lg overflow-hidden"
              style={{ aspectRatio: "16/9" }}
            >
              <video
                ref={videoRef}
                src={videoUrl}
                controls
                className="w-full h-full"
                onTimeUpdate={(e) => {
                  const video = e.target as HTMLVideoElement;
                  setCurrentTime(video.currentTime);
                }}
              >
                {t("Seu navegador não suporta a tag de vídeo.")}
              </video>

              {/* Overlay de textos */}
              <div className="absolute inset-0 pointer-events-none">
                {videoTexts
                  .filter(
                    (text) =>
                      currentTime >= text.startTime &&
                      currentTime <= text.endTime,
                  )
                  .map((text) => {
                    const positionClasses: Record<string, string> = {
                      "top-left": "top-4 left-4",
                      "top-center": "top-4 left-1/2 -translate-x-1/2",
                      "top-right": "top-4 right-4",
                      "bottom-left": "bottom-4 left-4",
                      "bottom-center": "bottom-4 left-1/2 -translate-x-1/2",
                      "bottom-right": "bottom-4 right-4",
                      center:
                        "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
                    };

                    return (
                      <div
                        key={text.id}
                        className={`absolute ${positionClasses[text.position] || "bottom-4 left-1/2 -translate-x-1/2"}`}
                        style={{
                          fontSize: `${text.fontSize || 24}px`,
                          color: text.color || "#FFFFFF",
                          textShadow: "2px 2px 4px rgba(0,0,0,0.8)",
                          fontWeight: "bold",
                          textAlign: "center",
                          maxWidth: "90%",
                          wordWrap: "break-word",
                        }}
                      >
                        {text.text}
                      </div>
                    );
                  })}
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {t(
                "Os textos aparecerão sobrepostos no vídeo durante a reprodução. Use os controles do vídeo para verificar em diferentes momentos.",
              )}
            </p>
          </div>
        )}

        {/* Seção de Textos/Legendas */}
        <div className="border-b pb-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Type className="w-5 h-5" />
              {t("Textos e Legendas do Vídeo")}
            </h3>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                icon={
                  extractingTexts ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Scan className="w-4 h-4" />
                  )
                }
                onClick={handleExtractTexts}
                disabled={extractingTexts || loading || !videoUrl}
              >
                {extractingTexts ? t("Extraindo...") : t("Extrair Textos")}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                icon={<Plus className="w-4 h-4" />}
                onClick={handleAddText}
                disabled={saving || loading}
              >
                {t("Adicionar Texto")}
              </Button>
            </div>
          </div>

          {videoTexts.length > 0 ? (
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {videoTexts.map((textItem) => (
                <div
                  key={textItem.id}
                  className="p-3 border border-purple-300 rounded-lg space-y-2 bg-gray-50"
                >
                  <div className="flex justify-between items-start gap-2">
                    <Input
                      type="text"
                      value={textItem.text}
                      onChange={(e) =>
                        handleUpdateText(textItem.id, "text", e.target.value)
                      }
                      placeholder={t("Texto a exibir no vídeo")}
                      inputSize="md"
                      disabled={saving || loading}
                      className="flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveText(textItem.id)}
                      className="text-red-500 hover:text-red-700 p-1"
                      disabled={saving || loading}
                      aria-label={t("Remover texto")}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-4 gap-2">
                    <div>
                      <label className="text-xs text-gray-600 mb-1 block">
                        {t("Início (s)")}
                      </label>
                      <Input
                        type="number"
                        value={textItem.startTime}
                        onChange={(e) =>
                          handleUpdateText(
                            textItem.id,
                            "startTime",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        inputSize="sm"
                        disabled={saving || loading}
                        min="0"
                        max={videoDuration}
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">
                        {t("Fim (s)")}
                      </label>
                      <Input
                        type="number"
                        value={textItem.endTime}
                        onChange={(e) =>
                          handleUpdateText(
                            textItem.id,
                            "endTime",
                            parseFloat(e.target.value) || videoDuration,
                          )
                        }
                        inputSize="md"
                        disabled={saving || loading}
                        min="0"
                        max={videoDuration}
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">
                        {t("Posição")}
                      </label>
                      <Select
                        options={[
                          { value: "top-left", label: t("Topo Esquerda") },
                          { value: "top-center", label: t("Topo Centro") },
                          { value: "top-right", label: t("Topo Direita") },
                          {
                            value: "bottom-left",
                            label: t("Baixo Esquerda"),
                          },
                          {
                            value: "bottom-center",
                            label: t("Baixo Centro"),
                          },
                          {
                            value: "bottom-right",
                            label: t("Baixo Direita"),
                          },
                          { value: "center", label: t("Centro") },
                        ]}
                        value={textItem.position}
                        onChange={(v) =>
                          handleUpdateText(textItem.id, "position", v)
                        }
                        size="md"
                        disabled={saving || loading}
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">
                        {t("Tamanho")}
                      </label>
                      <Input
                        type="number"
                        value={textItem.fontSize || 24}
                        onChange={(e) =>
                          handleUpdateText(
                            textItem.id,
                            "fontSize",
                            parseInt(e.target.value) || 24,
                          )
                        }
                        inputSize="md"
                        disabled={saving || loading}
                        min="12"
                        max="72"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">
              {t(
                'Nenhum texto adicionado. Clique em "Adicionar Texto" para criar legendas ou overlays.',
              )}
            </p>
          )}
        </div>

        {/* Seção de Textos Detectados do Vídeo */}
        {detectedTexts.length > 0 && (
          <div className="border-b pb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-3">
              <Scan className="w-5 h-5" />
              {t("Textos Detectados no Vídeo")}
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto bg-blue-50 p-3 rounded-sm">
              {detectedTexts.map((detected, index) => (
                <div key={index} className="text-sm">
                  <span className="font-medium">
                    {t("Tempo")} {detected.start_time?.toFixed(1)}s -{" "}
                    {detected.end_time?.toFixed(1)}s:
                  </span>
                  <span className="ml-2">{detected.text}</span>
                  <span className="ml-2 text-xs text-gray-500">
                    (
                    {t("Confiança: {value}%", {
                      value: (detected.confidence * 100).toFixed(0),
                    })}
                    )
                  </span>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {t(
                'Estes textos foram detectados automaticamente no vídeo. Você pode editá-los na seção "Textos e Legendas" acima.',
              )}
            </p>
          </div>
        )}

        {/* Seção de Script e Pronúncia */}
        <div className="border-b pb-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Volume2 className="w-5 h-5" />
              {t("Script de Narração e Pronúncia")}
            </h3>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                icon={
                  transcribing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mic className="w-4 h-4" />
                  )
                }
                onClick={handleTranscribeAudio}
                disabled={transcribing || loading || !videoUrl}
              >
                {transcribing ? t("Transcrevendo...") : t("Transcrever Áudio")}
              </Button>
              {transcription && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  icon={
                    regeneratingAudio ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )
                  }
                  onClick={handleRegenerateAudio}
                  disabled={
                    regeneratingAudio || loading || !editedTranscription.trim()
                  }
                >
                  {regeneratingAudio
                    ? t("Regenerando...")
                    : t("Regenerar Áudio")}
                </Button>
              )}
            </div>
          </div>

          {transcription && (
            <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-sm">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("Texto Transcrito do Áudio (Edite para corrigir):")}
              </label>
              <Textarea
                value={editedTranscription}
                onChange={(e) => {
                  setEditedTranscription(e.target.value);
                  setVideoScript(e.target.value);
                }}
                placeholder={t("Texto transcrito do áudio do vídeo...")}
                inputSize="md"
                disabled={saving || loading}
                rows={4}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t(
                  'Edite o texto acima e clique em "Regenerar Áudio" para criar um novo áudio com as correções.',
                )}
              </p>
            </div>
          )}

          <Textarea
            label={t("Script de Narração")}
            value={videoScript}
            onChange={(e) => setVideoScript(e.target.value)}
            placeholder={t(
              "Digite ou edite o texto que será narrado no vídeo...",
            )}
            inputSize="md"
            disabled={saving || loading}
            rows={6}
            hint={t(
              "Edite o texto para corrigir o conteúdo da narração. Use a seção abaixo para corrigir pronúncias específicas.",
            )}
          />

          {videoScript && scriptWords.length > 0 && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("Guia de Pronúncia (Opcional)")}
              </label>
              <p className="text-xs text-gray-500 mb-3">
                {t(
                  'Corrija a pronúncia de palavras específicas. Exemplo: "marketing" → "márqueting"',
                )}
              </p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {scriptWords.slice(0, 20).map((word) => (
                  <div key={word} className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700 w-32 truncate">
                      {word}:
                    </span>
                    <Input
                      type="text"
                      value={pronunciationGuide[word] || ""}
                      onChange={(e) =>
                        handleUpdatePronunciation(word, e.target.value)
                      }
                      placeholder={t('Pronúncia para "{word}"', { word })}
                      inputSize="md"
                      disabled={saving || loading}
                      className="flex-1"
                    />
                  </div>
                ))}
              </div>
              {scriptWords.length > 20 && (
                <p className="text-xs text-gray-500 mt-2">
                  {t(
                    "Mostrando 20 primeiras palavras. Adicione mais palavras manualmente se necessário.",
                  )}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Informações */}
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-sm text-blue-800">
          <p className="font-semibold mb-1">💡 {t("Dicas:")}</p>
          <ul className="list-disc list-inside space-y-1 text-xs">
            <li>
              {t(
                "Os textos serão sobrepostos ao vídeo nas posições e horários especificados",
              )}
            </li>
            <li>
              {t("O script será convertido em fala usando text-to-speech")}
            </li>
            <li>
              {t(
                "Use o guia de pronúncia para corrigir palavras que podem ser mal pronunciadas",
              )}
            </li>
            <li>{t("As alterações serão aplicadas ao reprocessar o vídeo")}</li>
          </ul>
        </div>
      </div>
    </Modal>
  );
};

export default VideoEditorModal;
